import {
  useGetCanonicalCategoriesByEvent,
  useGetCanonicalTagsByEvent,
  useGetSessionCategories,
  useGetSessionTags,
} from '@sunrise-ui/api-client';
import {
  Colors,
  Tag as TagComponent,
  TagSkeletons,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import React, { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';

type Props = {
  sessionId: string;
  eventId?: string;
  showAllTags?: boolean;
};

// Define unified types for tag display items
interface DisplayTagItem {
  itemType: 'tag' | 'category';
  id: string;
  name: string;
}

// Import proper API types to ensure type safety
import type { SessionCategory, SessionTag } from '@sunrise-ui/api/events/api';

const OVERFLOW_PILL_SMALL_WIDTH = 50.47;
const OVERFLOW_PILL_LARGE_WIDTH = 58.52;
const TAG_GAP = 8;

export function CacheAwareSessionCardTags({
  sessionId,
  eventId,
  showAllTags = false,
}: Props) {
  // Use individual session hooks with built-in cache awareness
  const { data: sessionTags, isLoading: isLoadingSessionTags } =
    useGetSessionTags(sessionId);
  const { data: sessionCategories, isLoading: isLoadingSessionCategories } =
    useGetSessionCategories(sessionId);

  // Get canonical tags and categories for cross-referencing
  const { data: eventTags, isLoading: isLoadingEventTags } =
    useGetCanonicalTagsByEvent(eventId);
  const { data: eventCategories, isLoading: isLoadingEventCategories } =
    useGetCanonicalCategoriesByEvent(eventId);

  // Loading state includes both session data and canonical data
  // Only show loading if we're actually loading AND don't have data yet
  const isLoadingTags =
    isLoadingSessionTags ||
    isLoadingSessionCategories ||
    isLoadingEventTags ||
    isLoadingEventCategories;

  const [containerWidth, setContainerWidth] = useState(0);
  const [tagsWidth, setTagsWidth] = useState<Record<string, number>>({});

  // Combine tags and categories into a single array for display, preserving their type
  const tagItems = useMemo<DisplayTagItem[]>(() => {
    if (!sessionTags || !sessionCategories || !eventTags || !eventCategories)
      return [];

    // Process tags using canonical event tags for lookup
    const tagItemsWithType: DisplayTagItem[] = sessionTags.map(
      (sessionTag: SessionTag) => {
        // Use proper SessionTag type - no casting needed
        const tagId = sessionTag.tag_id;

        // Look up tag name from canonical event tags
        const canonicalTag = eventTags?.find((tag) => tag.id === tagId);
        // Use canonical tag name as fallback
        const tagName = canonicalTag?.name || '';

        return {
          itemType: 'tag',
          id: tagId,
          name: tagName,
        };
      },
    );

    // Process categories using canonical event categories for lookup
    const categoryItemsWithType: DisplayTagItem[] = sessionCategories.map(
      (sessionCategory: SessionCategory) => {
        // Use proper SessionCategory type - no casting needed
        const categoryId = sessionCategory.category_id;

        // Look up category name from canonical event categories
        const canonicalCategory = eventCategories?.find(
          (category) => category.id === categoryId,
        );
        // Use canonical category name as fallback
        const categoryName = canonicalCategory?.name || '';

        return {
          itemType: 'category',
          id: categoryId,
          name: categoryName,
        };
      },
    );

    return [...categoryItemsWithType, ...tagItemsWithType] as DisplayTagItem[];
  }, [sessionTags, sessionCategories, eventTags, eventCategories]);

  const { numberOfTagsToShow, numberOfHiddenTags } = useMemo(() => {
    const numberOfTags = tagItems?.length ?? 0;

    if (isLoadingTags || !tagItems) {
      return {
        numberOfTagsToShow: 0,
        numberOfHiddenTags: 0,
      };
    }

    // Check if we have container width and all measurements
    const hasContainerWidth = containerWidth > 0;
    const measurementsCount = Object.keys(tagsWidth).length;
    const hasMeasurements = measurementsCount === numberOfTags;

    // If no container width, keep in measurement phase
    if (!hasContainerWidth) {
      return {
        numberOfTagsToShow: numberOfTags,
        numberOfHiddenTags: 0,
      };
    }

    // If showAllTags is true, show all tags without truncation
    if (!hasMeasurements || showAllTags) {
      return {
        numberOfTagsToShow: numberOfTags,
        numberOfHiddenTags: 0,
      };
    }

    let numberOfTagsToShow = 0;
    let tagsWidthSum = 0;

    // Calculate how many tags can fit in a single row
    for (let i = 0; i < numberOfTags; i++) {
      const width = tagsWidth[tagItems[i].id!];
      const overflowPillWidth =
        numberOfTags - numberOfTagsToShow > 1
          ? numberOfTags - numberOfTagsToShow < 10
            ? OVERFLOW_PILL_SMALL_WIDTH
            : OVERFLOW_PILL_LARGE_WIDTH
          : 0;

      // Check if adding this tag would exceed container width
      // Account for overflow pill if there are remaining tags
      const remainingTags = numberOfTags - numberOfTagsToShow - 1;
      const needsOverflowPill = remainingTags > 0;
      const requiredWidth =
        tagsWidthSum +
        width +
        (needsOverflowPill ? overflowPillWidth + TAG_GAP : 0);

      if (requiredWidth > containerWidth) break;

      tagsWidthSum += width + TAG_GAP;
      numberOfTagsToShow += 1;
    }

    return {
      numberOfTagsToShow,
      numberOfHiddenTags: numberOfTags - numberOfTagsToShow,
    };
  }, [containerWidth, tagsWidth, isLoadingTags, tagItems, showAllTags]);

  const styles = useDynamicStyles(createStyles);

  if (!isLoadingTags && !tagItems?.length) return null;

  return (
    <View
      style={styles.tagsContainer}
      onLayout={(e) => {
        setContainerWidth(e.nativeEvent.layout.width);
      }}
    >
      <View style={[styles.row, showAllTags && styles.rowWrap]}>
        {isLoadingTags ? (
          <TagSkeletons count={2} />
        ) : (
          <>
            {tagItems?.map((item, index) => {
              const key = `${sessionId}-${item.id}-${index}`;
              return (
                <React.Fragment key={key}>
                  <TagComponent
                    id={item.id}
                    name={item.name}
                    type={item.itemType}
                    isVisible={index < numberOfTagsToShow}
                    onLayout={(e) => {
                      const { width } = e.nativeEvent.layout;
                      setTagsWidth((prev) => ({
                        ...prev,
                        [item.id!]: width,
                      }));
                    }}
                  />
                  {numberOfHiddenTags > 0 &&
                    index === numberOfTagsToShow - 1 && (
                      <TagComponent
                        style={styles.overflowPill}
                        name={`+${numberOfHiddenTags} more`}
                      />
                    )}
                </React.Fragment>
              );
            })}
          </>
        )}
      </View>
    </View>
  );
}

const createStyles = () =>
  StyleSheet.create({
    tagsContainer: {
      flexShrink: 1,
      width: '100%',
      justifyContent: 'space-between',
      gap: TAG_GAP,
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      gap: TAG_GAP,
    },
    rowWrap: {
      flexWrap: 'wrap',
    },
    overflowPill: {
      paddingVertical: 2,
      backgroundColor: Colors.purple0,
    },
    tagText: {
      fontSize: 12,
    },
  });
